import { keyframes, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { typographyVariantStyles } from '../../typography-variants';
import type { HelperState } from './types';

const ICON_SIZE = 16;
const LINE_HEIGHT = 20;
const REVEALED_MAX_HEIGHT = 120;

function getColor(theme: Theme, state: HelperState): string {
  switch (state) {
    case 'destructive':
      return theme.colors.text.error;
    case 'success':
      return theme.colors.success.text;
    case 'warning':
      return theme.colors.warning.text;
    default:
      return theme.colors.text.secondary;
  }
}

const reveal = keyframes({
  from: {
    opacity: 0,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    transform: 'translateY(-4px)',
  },
  to: {
    opacity: 1,
    maxHeight: REVEALED_MAX_HEIGHT,
    paddingTop: 2,
    paddingBottom: 2,
    transform: 'none',
  },
});

const collapse = keyframes({
  from: {
    opacity: 1,
    maxHeight: REVEALED_MAX_HEIGHT,
    paddingTop: 2,
    paddingBottom: 2,
    transform: 'none',
  },
  to: {
    opacity: 0,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    transform: 'translateY(-4px)',
  },
});

export const StyledHelper = styled('div', {
  shouldForwardProp: (prop) => prop !== '$state' && prop !== '$exiting',
})<{ $state: HelperState; $exiting: boolean }>(({ theme, $state, $exiting }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  padding: '2px 0',
  maxWidth: '100%',
  overflow: 'hidden',
  overflowWrap: 'anywhere',
  color: getColor(theme, $state),
  animation: `${$exiting ? collapse : reveal} ${theme.motion.reveal}`,
  animationFillMode: $exiting ? 'forwards' : 'none',
  ...typographyVariantStyles.body3,

  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

export const StyledIconWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: ICON_SIZE,
  height: LINE_HEIGHT,
  color: 'inherit',

  '& svg': {
    width: ICON_SIZE,
    height: ICON_SIZE,
  },
});
